import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { cn } from "../lib/utils";
import { formatInr } from "../lib/accountsSettlement";
import { formatWeightKg } from "../lib/weightUnits";
import { Pill } from "./Ui";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import TallySyncBadge from "./TallySyncBadge";
import TallySyncMappingDialog from "./TallySyncMappingDialog";
import { tallyDirectSyncBlockedReason } from "../lib/tallyApproval";
import VoucherDocumentActions from "./VoucherDocumentActions";
import { toast } from "sonner";

export function forBillableQtl(millQtl, partyQtl) {
  const mill = Number(millQtl) || 0;
  const party = Number(partyQtl);
  if (!(party > 0)) return mill;
  return Math.min(mill, party);
}

export function seedCommercialForm(dispatch = {}, settlement = {}, contract = {}) {
  const opts = dispatch.settlement_options || {};
  const snap = (dispatch.provisional_snapshot?.allocations || dispatch.final_snapshot?.allocations || [])[0] || {};
  const src = { ...contract, ...snap, ...settlement, ...opts };
  const num = (v) => (v != null && v !== "" ? String(v) : "");
  return {
    cd_applicable: src.cd_applicable != null ? Boolean(src.cd_applicable) : Boolean(src.cd_percent),
    cd_percent: num(src.cd_percent),
    broker_applicable: src.broker_applicable != null ? Boolean(src.broker_applicable) : Boolean(src.brokerage || src.brokerage_rate),
    brokerage: num(src.brokerage ?? src.brokerage_rate),
    brokerage_basis: src.brokerage_basis || "GROSS_PCT",
    deduct_brokerage_from_receivable: Boolean(src.deduct_brokerage_from_receivable || src.use_rate_after_deductions),
    bardana_gm: num(src.bardana_gm ?? src.bardana_gm),
    bardana_basis: src.bardana_basis || "PER_BAG",
    apply_bag_cuts: src.apply_bag_cuts != null ? Boolean(src.apply_bag_cuts) : src.use_weight_after_bardana !== false,
    bag_cut_jute_kg: num(src.bag_cut_jute_kg ?? src.bag_cut_jute_kg),
    bag_cut_ppe_kg: num(src.bag_cut_ppe_kg ?? src.bag_cut_ppe_kg),
    oil_base_pct: num(src.oil_base_pct ?? src.oil_base_pct),
    gst_rate: num(src.gst_rate),
    gst_type: src.gst_type || "NONE",
    freight_rate_per_qtl: num(src.freight_rate_per_qtl),
    charge_freight_in_invoice: Boolean(src.charge_freight_in_invoice),
    use_rate_after_deductions: Boolean(src.use_rate_after_deductions || src.deduct_brokerage_from_receivable),
    use_weight_after_bardana: src.use_weight_after_bardana !== false,
  };
}

export function commercialPayload(form = {}) {
  const num = (v) => (v === "" || v == null ? undefined : Number(v));
  return {
    cd_applicable: Boolean(form.cd_applicable),
    cd_percent: num(form.cd_percent),
    broker_applicable: Boolean(form.broker_applicable),
    brokerage: num(form.brokerage),
    brokerage_basis: form.brokerage_basis || undefined,
    deduct_brokerage_from_receivable: Boolean(form.deduct_brokerage_from_receivable),
    bardana_gm: num(form.bardana_gm),
    bardana_basis: form.bardana_basis || undefined,
    apply_bag_cuts: form.apply_bag_cuts !== false,
    bag_cut_jute_kg: num(form.bag_cut_jute_kg),
    bag_cut_ppe_kg: num(form.bag_cut_ppe_kg),
    oil_base_pct: num(form.oil_base_pct),
    gst_rate: num(form.gst_rate),
    gst_type: form.gst_type || "NONE",
    freight_rate_per_qtl: num(form.freight_rate_per_qtl),
    charge_freight_in_invoice: Boolean(form.charge_freight_in_invoice),
    use_rate_after_deductions: Boolean(form.deduct_brokerage_from_receivable || form.use_rate_after_deductions),
    use_weight_after_bardana: form.apply_bag_cuts !== false,
  };
}

export function oilPctWarning(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return "Enter a numeric oil %.";
  if (n < 1 || n > 40) return "Oil % should be 1–40. Values outside this range fall back to contract oil base.";
  return null;
}

function WeightStat({ label, value, highlight, hint }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-sm font-medium tabular mt-0.5 break-words ${highlight ? "text-primary" : "text-foreground"}`}>
        {value}
        {hint ? <span className="text-[10px] font-normal text-muted-foreground ml-1">({hint})</span> : null}
      </p>
    </div>
  );
}

/** Mill vs party vs FOR billable — same card language as purchase TruckWeightSummary. */
export function CommercialWeightBar({
  millQtl,
  partyQtl,
  billableQtl,
  bags,
  deliveryCondition,
  partyEditable = false,
  partyValue,
  onPartyChange,
  className = "",
}) {
  const mill = millQtl != null && millQtl !== "" ? Number(millQtl) : null;
  const party = partyValue !== undefined && partyValue !== ""
    ? Number(partyValue)
    : (partyQtl != null && partyQtl !== "" ? Number(partyQtl) : null);
  const isFor = String(deliveryCondition || "").toUpperCase() === "FOR";
  const computedBillable = isFor ? forBillableQtl(mill, party) : mill;
  const shownBillable = billableQtl != null && billableQtl !== "" ? Number(billableQtl) : computedBillable;

  return (
    <div
      className={cn("grid grid-cols-2 md:grid-cols-4 gap-2 rounded-md border border-border bg-muted/20 p-3 min-w-0", className)}
      data-testid="sales-commercial-weight-bar"
    >
      <WeightStat label="Mill net" value={mill != null ? `${mill.toFixed(2)} qtl` : "—"} />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Party reported</p>
        {partyEditable ? (
          <Input
            type="number"
            step="0.01"
            min="0"
            className="mt-0.5 h-8 text-sm tabular"
            value={partyValue ?? ""}
            placeholder="Party qtl"
            onChange={(e) => onPartyChange?.(e.target.value)}
            data-testid="sales-party-weight-input"
          />
        ) : (
          <p className={`text-sm font-medium tabular mt-0.5 ${party > 0 ? "text-primary" : "text-foreground"}`}>
            {party > 0 ? `${party.toFixed(2)} qtl` : "—"}
          </p>
        )}
      </div>
      <WeightStat label="Bags" value={bags ?? "—"} />
      <WeightStat
        label="Billable net"
        value={shownBillable != null && Number.isFinite(shownBillable) ? `${Number(shownBillable).toFixed(2)} qtl` : "—"}
        highlight={shownBillable > 0}
        hint={isFor ? "min(mill, party)" : "mill net"}
      />
    </div>
  );
}

export function SalesVoucherSummaryRibbon({ totals = {}, className = "" }) {
  const goods = Number(totals.full_amount || 0);
  const freight = Number(totals.freight_on_invoice || 0);
  const deductions = Number(totals.cd_amount || 0)
    + Number(totals.brokerage_amount || 0)
    + Number(totals.ffa_amount || 0)
    + Number(totals.other_deductions_total || 0);
  const additions = Number(totals.other_additions_total || 0);
  const gst = Number(totals.gst_amount || 0);
  const invoice = totals.invoice_total != null ? Number(totals.invoice_total) : Number(totals.receivable || 0) + gst;

  return (
    <div
      className={cn("rounded-md border border-bansal-sage/30 bg-bansal-sageLight/20 dark:bg-bansal-sage/10 px-3 py-2.5 space-y-1", className)}
      data-testid="sales-voucher-summary-ribbon"
    >
      <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
        Net invoice payable by buyer
      </p>
      <p className="text-xs text-muted-foreground leading-relaxed">
        <span className="tabular text-foreground font-medium">{formatInr(goods)}</span>
        {" goods"}
        {freight > 0 ? <>{" + "}<span className="tabular text-foreground font-medium">{formatInr(freight)}</span>{" freight"}</> : null}
        {deductions > 0 ? <>{" − "}<span className="tabular text-foreground font-medium">{formatInr(deductions)}</span>{" deductions"}</> : null}
        {additions > 0 ? <>{" + "}<span className="tabular text-foreground font-medium">{formatInr(additions)}</span>{" additions"}</> : null}
        {gst > 0 ? <>{" + "}<span className="tabular text-foreground font-medium">{formatInr(gst)}</span>{" GST"}</> : null}
        {" = "}
        <span className="tabular font-semibold text-primary">{formatInr(invoice)}</span>
      </p>
    </div>
  );
}

export function SalesWorkspaceHeader({
  dispatch,
  voucherMode = "PROVISIONAL",
  canEdit = false,
  saving = false,
  onSaveDraft,
  onPreviewTally,
  onFinalize,
  finalizeLabel = "Finalize & Sync",
}) {
  const code = dispatch?.sales_voucher_code || dispatch?.code || "—";
  const tallyStatus = dispatch?.tally_sync_status
    || dispatch?.sales_voucher?.tally_sync_status
    || "NOT_SYNCED";
  const isFinal = String(voucherMode || dispatch?.sales_settlement_status || "").toUpperCase() === "FINAL";

  return (
    <div className="min-w-0 rounded-xl lg:rounded-md border border-border bg-muted/35 p-3 sm:p-4 space-y-3" data-testid="sales-workspace-header">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-mono text-sm font-semibold tracking-tight text-foreground">{code}</p>
            <Pill tone={isFinal ? "sage" : "warning"}>{isFinal ? "Final settlement" : "Provisional sales"}</Pill>
            <TallySyncBadge status={tallyStatus} error={dispatch?.tally_error_message} showDot={false} />
          </div>
          <p className="text-sm font-semibold mt-1 break-words">{dispatch?.party_name || "—"}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {dispatch?.vehicle_number ? <span className="font-mono">{dispatch.vehicle_number}</span> : "—"}
            {dispatch?.sales_contract_number ? ` · ${dispatch.sales_contract_number}` : ""}
            {dispatch?.code && dispatch?.sales_voucher_code ? ` · Dispatch ${dispatch.code}` : ""}
            {dispatch?.party_id ? (
              <>
                {" · "}
                <Link to={`/parties/${dispatch.party_id}/ledger`} className="text-primary hover:underline font-medium">
                  View ledger
                </Link>
              </>
            ) : null}
          </p>
        </div>
        {(canEdit || onPreviewTally) ? (
          <div className="flex flex-wrap items-center gap-2 justify-end">
            {canEdit && onSaveDraft ? (
              <Button type="button" size="sm" variant="outline" className="h-8 text-xs" disabled={saving} onClick={onSaveDraft} data-testid="sales-save-draft">
                {saving ? "Saving…" : "Save Draft"}
              </Button>
            ) : null}
            {onPreviewTally ? (
              <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={onPreviewTally} data-testid="sales-preview-tally">
                Preview Tally
              </Button>
            ) : null}
            {canEdit && onFinalize ? (
              <Button
                type="button"
                size="sm"
                className="h-8 text-xs bg-bansal-sage hover:bg-bansal-sageDark text-white"
                disabled={saving}
                onClick={onFinalize}
                data-testid="sales-finalize-sync"
              >
                {finalizeLabel}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Queue card matching purchase voucher register identity + amount + Tally badge. */
export function SalesDispatchQueueCard({ d, selected, onSelect, showVoucherActions = false }) {
  const freightStatus = String(d.freight_status || "").toUpperCase();
  const showFreight = Boolean(d.freight_voucher_id) && freightStatus !== "WAIVED";
  const tallyStatus = d.tally_sync_status || d.sales_voucher?.tally_sync_status;
  const settlement = String(d.sales_settlement_status || "").toUpperCase();
  const secondaryMeta = [
    d.gate_pass_code ? `GP ${d.gate_pass_code}` : null,
    d.sales_contract_number || null,
    d.net_weight_qtl != null ? `${d.net_weight_qtl} qtl` : (d.net_weight_qtl != null ? formatWeightKg(d.net_weight_qtl) : null),
    d.bags_loaded != null ? `${d.bags_loaded} bags` : null,
  ].filter(Boolean);
  const statusPills = [
    settlement === "PROVISIONAL" ? { key: "prov", label: "Provisional" } : null,
    settlement === "FINAL" ? { key: "final", label: "Final" } : null,
    d.accounts_reopen ? { key: "reopen", label: "Changed" } : null,
    showFreight && freightStatus !== "FINALIZED" ? { key: "frt", label: "Frt draft" } : null,
  ].filter(Boolean);

  return (
    <div
      className={cn(
        "w-full rounded-lg border transition-colors duration-150 overflow-hidden",
        selected
          ? "border-sky-400 bg-sky-50/70 ring-1 ring-sky-200/80 dark:bg-sky-950/40 dark:ring-sky-800/60"
          : "border-border/80 bg-card/70 opacity-90 hover:opacity-100 hover:border-bansal-sage/40 hover:bg-muted/25",
      )}
      data-testid={`accounts-sales-queue-${d.id}`}
    >
      <button type="button" onClick={onSelect} className="w-full text-left px-3 py-2.5 space-y-2">
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <p className="font-mono text-[13px] font-semibold tracking-tight text-foreground whitespace-nowrap">
              {d.sales_voucher_code || d.code}
            </p>
            {tallyStatus ? (
              <TallySyncBadge status={tallyStatus} showDot={false} className="shrink-0 items-end" />
            ) : null}
          </div>
          <p className="text-[13px] font-semibold text-foreground break-words hyphens-none leading-snug">
            {d.party_name || "—"}
          </p>
          {d.vehicle_number ? (
            <p className="font-mono text-[12px] font-medium tracking-wide text-foreground/85 whitespace-nowrap">
              {d.vehicle_number}
            </p>
          ) : null}
        </div>

        {statusPills.length ? (
          <div className="flex flex-wrap gap-1">
            {statusPills.map((pill) => (
              <Pill key={pill.key} tone="warning">{pill.label}</Pill>
            ))}
          </div>
        ) : null}

        {d.sales_voucher_net != null || d.sales_voucher_gross != null ? (
          <div className="mt-1 rounded-md border border-bansal-sage/20 bg-bansal-sageLight/25 px-2.5 py-1.5 space-y-0.5">
            <p className="text-[12px] font-semibold tabular text-foreground leading-snug">
              <span className="text-[9px] uppercase tracking-[0.12em] text-primary/90 font-medium mr-1.5">Sales</span>
              {formatInr(d.sales_voucher_net ?? d.sales_voucher_gross)}
            </p>
            {showFreight && d.freight_voucher?.net_payable != null ? (
              <p className="text-[12px] font-semibold tabular text-foreground leading-snug">
                <span className="text-[9px] uppercase tracking-[0.12em] text-amber-800/90 font-medium mr-1.5">Freight</span>
                {formatInr(d.freight_voucher.net_payable)}
              </p>
            ) : null}
          </div>
        ) : null}

        {secondaryMeta.length ? (
          <p className="text-[10px] text-muted-foreground leading-snug break-words">{secondaryMeta.join(" · ")}</p>
        ) : null}
      </button>

      {showVoucherActions && d.sales_voucher_id ? (
        <div
          className="px-3 pb-2.5 pt-2 border-t border-border/50 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-x-5 sm:gap-y-2"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <VoucherDocumentActions label="Sales" voucherId={d.sales_voucher_id} voucherCode={d.sales_voucher_code} />
          {showFreight ? (
            <VoucherDocumentActions label="Freight" voucherId={d.freight_voucher_id} voucherCode={d.freight_voucher_code} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function TallyPreviewDialog({ open, onOpenChange, voucherIds = [] }) {
  const [loading, setLoading] = useState(false);
  const [chunks, setChunks] = useState([]);

  const ids = useMemo(
    () => (voucherIds || []).map((row) => (typeof row === "number" ? { id: row, code: `#${row}` } : row)).filter((r) => r?.id),
    [voucherIds],
  );
  const key = ids.map((t) => t.id).join(",");

  useEffect(() => {
    if (!open || !ids.length) return undefined;
    let cancelled = false;
    setLoading(true);
    setChunks([]);
    Promise.all(
      ids.map((t) =>
        api.get(`/tally/vouchers/${t.id}/preview`).then(({ data }) => ({ target: t, data })).catch((err) => ({
          target: t,
          data: { ok: false, errors: [extractError(err)] },
        })),
      ),
    )
      .then((rows) => {
        if (!cancelled) setChunks(rows);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, key]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sales-tally-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview Tally XML</DialogTitle>
          <DialogDescription>
            Structured Debit (party ledger) and Credit (sales / freight / GST) journal legs — not posted until you sync.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading preview…</p>
        ) : (
          <div className="space-y-4">
            {chunks.map(({ target, data }) => (
              <div key={target.id} className="rounded-md border border-border bg-card p-3 space-y-2">
                <p className="font-mono text-sm font-semibold">
                  {data?.voucher_code || target.code}
                  {data?.voucher_type ? <span className="text-muted-foreground font-sans font-normal text-xs ml-2">{data.voucher_type}</span> : null}
                </p>
                {!data?.ok ? (
                  <p className="text-xs text-red-700">{(data?.errors || []).join("; ") || "Preview failed"}</p>
                ) : (
                  <>
                    {data.narration ? <p className="text-[11px] text-muted-foreground">{data.narration}</p> : null}
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                          <th className="text-left py-1 font-medium">Ledger</th>
                          <th className="text-right py-1 font-medium">Debit</th>
                          <th className="text-right py-1 font-medium">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.lines || []).map((ln, i) => (
                          <tr key={`${ln.tally_ledger_name}-${i}`} className="border-b border-border/50">
                            <td className="py-1.5 pr-2">
                              <span className="font-medium">{ln.tally_ledger_name || ln.label || "—"}</span>
                              {ln.debit ? <span className="ml-1.5 text-[10px] uppercase text-sky-800">Dr</span> : <span className="ml-1.5 text-[10px] uppercase text-emerald-800">Cr</span>}
                            </td>
                            <td className="py-1.5 text-right tabular">{ln.debit ? formatInr(ln.debit) : "—"}</td>
                            <td className="py-1.5 text-right tabular">{ln.credit ? formatInr(ln.credit) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="font-semibold">
                          <td className="pt-2">Total</td>
                          <td className="pt-2 text-right tabular">{formatInr(data.debit_total)}</td>
                          <td className="pt-2 text-right tabular">{formatInr(data.credit_total)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function openSalesTallyPreview(dispatch, setTargets) {
  const ids = [];
  if (dispatch?.sales_voucher_id) ids.push({ id: dispatch.sales_voucher_id, code: dispatch.sales_voucher_code });
  if (dispatch?.freight_voucher_id) ids.push({ id: dispatch.freight_voucher_id, code: dispatch.freight_voucher_code });
  if (dispatch?.adjustment_journal_id) {
    ids.push({ id: dispatch.adjustment_journal_id, code: dispatch.adjustment_journal_code });
  }
  if (!ids.length) {
    toast.error("No sales voucher to preview yet — save a draft first");
    return;
  }
  setTargets(ids);
}

export function SalesStageBanners({ dispatch }) {
  const status = String(dispatch?.sales_settlement_status || "").toUpperCase();
  if (!dispatch?.sales_voucher_id && !status) return null;
  const stage2 = status === "FINAL" || Boolean(dispatch?.adjustment_journal_id);
  return (
    <div className="space-y-2">
      <div
        className="rounded-md border border-border bg-card px-3 py-2 text-foreground"
        data-testid="sales-stage-1-banner"
      >
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          Stage 1 · Provisional sales booked
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-mono font-semibold">{dispatch?.sales_voucher_code || "—"}</span>
          <TallySyncBadge
            status={dispatch?.sales_tally_sync_status}
            error={dispatch?.tally_error_message}
            showDot={false}
          />
        </div>
      </div>
      <div
        className={`rounded-md border px-3 py-2 ${
          stage2 ? "border-border bg-card text-foreground" : "border-dashed border-border bg-muted/30 text-muted-foreground"
        }`}
        data-testid="sales-stage-2-banner"
      >
        <p className="text-[10px] uppercase tracking-widest font-semibold">
          Stage 2 · Final unloading settlement
        </p>
        {stage2 ? (
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground">
            <span className="font-mono font-semibold">{dispatch?.adjustment_journal_code || "No delta journal"}</span>
            {dispatch?.adjustment_journal_id ? (
              <TallySyncBadge
                status={dispatch?.adjustment_journal_tally_sync_status}
                showDot={false}
              />
            ) : (
              <span className="text-muted-foreground">Final amount matches mill-gate bill</span>
            )}
          </div>
        ) : (
          <p className="mt-1 text-xs">Enter party weight, quality cuts, and deductions. Mill-gate sales invoice stays frozen.</p>
        )}
      </div>
    </div>
  );
}

export function SalesAdjustmentBreakdown({ breakdown }) {
  const lines = Array.isArray(breakdown?.lines) ? breakdown.lines : [];
  if (!lines.length) return null;
  return (
    <div
      className="rounded-md border border-border bg-card overflow-x-auto"
      data-testid="sales-adjustment-breakdown"
    >
      <table className="w-full text-xs">
        <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-1.5 font-medium">Particular</th>
            <th className="text-right px-3 py-1.5 font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((ln) => {
            const sign = ln.sign || "";
            const amt = Number(ln.amount || 0);
            const shown = sign === "-" ? -Math.abs(amt) : amt;
            return (
              <tr key={ln.key || ln.label} className="border-t border-border/60">
                <td className="px-3 py-1.5 text-foreground">
                  {sign === "-" ? "(−) " : sign === "=" ? "(=) " : ""}
                  {ln.label}
                </td>
                <td
                  className={`px-3 py-1.5 text-right tabular-nums font-medium ${
                    sign === "=" ? "text-primary" : "text-foreground"
                  }`}
                >
                  {formatInr(shown)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function PostSettlementJournalButton({ dispatch, onSynced }) {
  const [open, setOpen] = useState(false);
  const journalId = dispatch?.adjustment_journal_id;
  const blocked = tallyDirectSyncBlockedReason({
    sync_approval_status: dispatch?.adjustment_journal_sync_approval_status,
    sync_rejection_reason: dispatch?.adjustment_journal_rejection_reason,
  });
  if (!journalId) return null;
  return (
    <>
      <span title={blocked || undefined} className="inline-flex">
        <Button
          type="button"
          size="sm"
          className="h-8 text-xs"
          disabled={Boolean(blocked)}
          onClick={() => setOpen(true)}
          data-testid="post-settlement-journal-tally"
        >
          Post Settlement Journal to Tally
        </Button>
      </span>
      {blocked ? (
        <Link to="/accounts/tally-approvals" className="text-[11px] underline text-muted-foreground ml-2">
          Tally approvals
        </Link>
      ) : null}
      <TallySyncMappingDialog
        open={open}
        onOpenChange={setOpen}
        voucherId={journalId}
        voucherCode={dispatch?.adjustment_journal_code}
        onSynced={() => {
          setOpen(false);
          onSynced?.();
        }}
      />
    </>
  );
}

/** Unused imports guard — Label is used by callers composing GST fields. */
export function GstFields({ form, onPatch }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">GST type</Label>
        <select
          className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-xs"
          value={form.gst_type || "NONE"}
          onChange={(e) => onPatch({ gst_type: e.target.value })}
        >
          <option value="NONE">No GST</option>
          <option value="CGST_SGST">CGST + SGST</option>
          <option value="IGST">IGST</option>
        </select>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">GST %</Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          className="mt-1 h-9"
          disabled={(form.gst_type || "NONE") === "NONE"}
          value={form.gst_rate ?? ""}
          onChange={(e) => onPatch({ gst_rate: e.target.value })}
          placeholder="e.g. 5"
        />
      </div>
    </div>
  );
}
